import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import {
  Save,
  Info,
  X,
  CheckCircle2,
  AlertCircle,
  KeyRound,
  ChevronDown,
  RotateCcw,
} from "lucide-react";
import "./HomebuyerAccountPage.css";
import { EUROPEAN_COUNTRIES } from "../../../data/countries";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const HB_ACCOUNT_FORM_STORAGE_KEY = "legup_hb_account_form_draft";
const COUNTRIES = [
  { code: "BE", label: "Belgium" },
  { code: "ES", label: "Spain" },
];

const MAX_PROFILE_IMAGE_SIZE_MB = 12;
const ACCEPTED_PROFILE_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  country_of_residence: "",
  national_id_number: "",
  preferred_language: "",
  address: "",
  nationality: "",
  date_of_birth: "",
  profile_image_file: null,
  profile_image_url: "",
  remove_profile_image: false,
};

function readStorage(key, fallback) {
  try {
    const saved = sessionStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
}

function writeStorage(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to save ${key}:`, error);
  }
}

function clearStorage(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to clear ${key}:`, error);
  }
}

function parsePersonalInfoDraft() {
  try {
    const raw = localStorage.getItem("legup_hb_dossier_personal_info");
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function normalizeMeToForm(data) {
  const account = data?.account || {};
  const profile = data?.profile || {};
  const draft = parsePersonalInfoDraft();

  return {
    first_name: account.first_name || draft.first_name || "",
    last_name: account.last_name || draft.last_name || "",
    email: account.email || draft.email || "",
    phone: profile.phone || draft.phone || "",
    country_of_residence:
      profile.country_of_residence || draft.country_of_residence || "",
    national_id_number:
      profile.national_id_number || draft.national_id_number || "",
    preferred_language: profile.preferred_language || "",
    address: profile.address || draft.address || "",
    nationality: profile.nationality || draft.nationality || "",
    date_of_birth: isoDateToDisplay(
      profile.date_of_birth || draft.date_of_birth || "",
    ),
    profile_image_file: null,
    profile_image_url:
      profile.profile_image_url || account.profile_image_url || "",
    remove_profile_image: false,
  };
}

function normalizeValue(value) {
  return typeof value === "string" ? value.trim() : value;
}

function buildChangedPayload(formData, initialData) {
  const payload = {};
  const homeownerProfile = {};

  const accountFields = ["first_name", "last_name"];

  const profileFields = [
    "phone",
    "country_of_residence",
    "national_id_number",
    "preferred_language",
    "address",
    "nationality",
    "date_of_birth",
  ];

  accountFields.forEach((field) => {
    const current = normalizeValue(formData[field]);
    const initial = normalizeValue(initialData[field]);

    if (current !== initial) {
      payload[field] = current;
    }
  });

  profileFields.forEach((field) => {
    let current = normalizeValue(formData[field]);
    let initial = normalizeValue(initialData[field]);

    if (current !== initial) {
      if (field === "date_of_birth") {
        current = displayDateToIso(current);
      }

      homeownerProfile[field] = current;
    }
  });

  if (Object.keys(homeownerProfile).length > 0) {
    payload.homeowner_profile = homeownerProfile;
  }

  return payload;
}

function buildChangedMultipartPayload(formData, initialData) {
  const multipartPayload = new FormData();

  const fields = [
    "first_name",
    "last_name",
    "phone",
    "country_of_residence",
    "national_id_number",
    "preferred_language",
    "address",
    "nationality",
    "date_of_birth",
  ];

  fields.forEach((field) => {
    const current = normalizeValue(formData[field]);
    const initial = normalizeValue(initialData[field]);

    if (current !== initial) {
      const valueToSend =
        field === "date_of_birth" ? displayDateToIso(current) : current;

      multipartPayload.append(field, valueToSend ?? "");
    }
  });

  if (formData.profile_image_file) {
    multipartPayload.append("profile_image", formData.profile_image_file);
  }

  if (formData.remove_profile_image) {
    multipartPayload.append("remove_profile_image", "true");
  }

  return multipartPayload;
}

function FieldLabel({ label, tooltip, htmlFor }) {
  return (
    <div className="hb-account-field__label-row">
      <label className="hb-account-field__label" htmlFor={htmlFor}>
        {label}
      </label>

      {tooltip ? (
        <span className="hb-account-tooltip" tabIndex={0} aria-label={tooltip}>
          <Info size={14} aria-hidden="true" />
          <span className="hb-account-tooltip__bubble">{tooltip}</span>
        </span>
      ) : null}
    </div>
  );
}

function formatDateInput(value) {
  // Keep numbers only
  const digits = value.replace(/\D/g, "").slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function isoDateToDisplay(value) {
  if (!value) return "";

  // Already DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    return value;
  }

  // YYYY-MM-DD -> DD/MM/YYYY
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    return value;
  }

  const [, year, month, day] = match;

  return `${day}/${month}/${year}`;
}

function displayDateToIso(value) {
  if (!value) return "";

  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (!match) {
    return value;
  }

  const [, day, month, year] = match;

  return `${year}-${month}-${day}`;
}

function isValidDisplayDate(value) {
  if (!value) return true;

  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (!match) {
    return false;
  }

  const [, dayString, monthString, yearString] = match;

  const day = Number(dayString);
  const month = Number(monthString);
  const year = Number(yearString);

  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

export default function HomebuyerAccountPage() {
  const { t } = useTranslation("homebuyer-account");
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: isAuthLoading,
  } = useAuth0();

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [initialData, setInitialData] = useState(EMPTY_FORM);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState("");
  const fileInputRef = React.useRef(null);
  const { refreshMe, setAccountDirty } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    type: "",
    title: "",
    text: "",
  });

  const [pendingNavigationPath, setPendingNavigationPath] = useState("");
  const [leaveModal, setLeaveModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: "",
    title: "",
    text: "",
    confirmLabel: "",
  });

  const hasChanges = useMemo(() => {
    const currentComparable = {
      ...formData,
      profile_image_file: formData.profile_image_file
        ? {
            name: formData.profile_image_file.name,
            size: formData.profile_image_file.size,
            type: formData.profile_image_file.type,
          }
        : null,
    };

    const initialComparable = {
      ...initialData,
      profile_image_file: initialData.profile_image_file
        ? {
            name: initialData.profile_image_file.name,
            size: initialData.profile_image_file.size,
            type: initialData.profile_image_file.type,
          }
        : null,
    };

    return (
      JSON.stringify(currentComparable) !== JSON.stringify(initialComparable)
    );
  }, [formData, initialData]);

  useEffect(() => {
    if (isLoading) return;

    if (hasChanges) {
      const {
        profile_image_file,
        profile_image_url,
        remove_profile_image,
        ...serializableForm
      } = formData;

      writeStorage(HB_ACCOUNT_FORM_STORAGE_KEY, serializableForm);
    } else {
      clearStorage(HB_ACCOUNT_FORM_STORAGE_KEY);
    }
  }, [formData, hasChanges, isLoading]);

  useEffect(() => {
    setAccountDirty?.(Boolean(hasChanges));
  }, [hasChanges, setAccountDirty]);

  useEffect(() => {
    return () => {
      setAccountDirty?.(false);
    };
  }, [setAccountDirty]);

  useEffect(() => {
    function handleDocumentClick(event) {
      if (!hasChanges) return;

      const anchor = event.target.closest("a[href]");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("mailto:"))
        return;

      const nextUrl = new URL(anchor.href);

      const nextPath = nextUrl.pathname + nextUrl.search + nextUrl.hash;
      if (location.pathname === "/hb/account" && nextPath !== "/hb/account") {
        event.preventDefault();
        event.stopPropagation();

        setPendingNavigationPath(nextPath);
        setLeaveModal(true);
      }
    }

    document.addEventListener("click", handleDocumentClick, true);

    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [hasChanges, location.pathname]);

  useEffect(() => {
    function handleBeforeUnload(event) {
      if (!hasChanges) return;

      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasChanges]);

  React.useEffect(() => {
    if (feedbackModal.open) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [feedbackModal.open]);

  useEffect(() => {
    async function loadAccountData() {
      if (!isAuthenticated) return;

      try {
        setIsLoading(true);

        const accessToken = await getAccessTokenSilently();
        const response = await fetch(`${API_BASE_URL}/api/homeowner/me/`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail ||
              t(
                "messages.loadError",
                "Could not load your account information.",
              ),
          );
        }

        const nextForm = normalizeMeToForm(data);
        const savedDraft = readStorage(HB_ACCOUNT_FORM_STORAGE_KEY, null);

        setFormData(
          savedDraft
            ? {
                ...nextForm,
                ...savedDraft,
                profile_image_file: null,
                profile_image_url: nextForm.profile_image_url,
                remove_profile_image: false,
              }
            : nextForm,
        );
        setInitialData(nextForm);
      } catch (error) {
        setFeedbackModal({
          open: true,
          type: "error",
          title: t("messages.errorTitle", "Something went wrong"),
          text:
            error.message ||
            t("messages.loadError", "Could not load your account information."),
        });
      } finally {
        setIsLoading(false);
      }
    }

    loadAccountData();
  }, [getAccessTokenSilently, isAuthenticated, t]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadAccountData() {
      if (isAuthLoading || !isAuthenticated) return;

      try {
        setIsLoading(true);

        const accessToken = await getAccessTokenSilently();

        const response = await fetch(`${API_BASE_URL}/api/homeowner/me/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        });

        // Continue normally...
      } catch (error) {
        if (error.name === "AbortError") return;

        // Show error...
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }

    loadAccountData();

    return () => controller.abort();
  }, [getAccessTokenSilently, isAuthenticated, isAuthLoading, t]);

  function closeFeedbackModal() {
    setFeedbackModal({
      open: false,
      type: "",
      title: "",
      text: "",
    });
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleDateOfBirthChange(event) {
    const formattedValue = formatDateInput(event.target.value);

    setFormData((prev) => ({
      ...prev,
      date_of_birth: formattedValue,
    }));
  }

  function validateForm() {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      return t(
        "messages.nameRequired",
        "First name and last name are required.",
      );
    }

    if (formData.date_of_birth && !isValidDisplayDate(formData.date_of_birth)) {
      return t(
        "messages.invalidDateOfBirth",
        "Please enter a valid date of birth in DD/MM/YYYY format.",
      );
    }

    if (!formData.email.trim()) {
      return t("messages.emailRequired", "Email is required.");
    }

    return "";
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text: validationError,
      });
      return;
    }

    try {
      setIsSaving(true);

      const accessToken = await getAccessTokenSilently();
      const payload = buildChangedPayload(formData, initialData);

      let response;

      if (formData.profile_image_file || formData.remove_profile_image) {
        const multipartPayload = buildChangedMultipartPayload(
          formData,
          initialData,
        );

        response = await fetch(`${API_BASE_URL}/api/homeowner/me/update/`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: multipartPayload,
        });
      } else {
        response = await fetch(`${API_BASE_URL}/api/homeowner/me/update/`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      }

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            t("messages.saveError", "Could not save your changes."),
        );
      }

      const nextForm = {
        ...formData,
        profile_image_file: null,
        profile_image_url:
          data?.profile?.profile_image_url ||
          data?.account?.profile_image_url ||
          "",
        remove_profile_image: false,
      };

      setFormData(nextForm);
      setInitialData(nextForm);
      clearStorage(HB_ACCOUNT_FORM_STORAGE_KEY);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setFeedbackModal({
        open: true,
        type: "success",
        title: t("messages.successTitle", "Information updated"),
        text: t(
          "messages.saveSuccess",
          "Your account information was updated successfully.",
        ),
      });

      if (refreshMe) {
        refreshMe?.();
      }
      setAccountDirty?.(false);
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t("messages.saveError", "Could not save your changes."),
      });
    } finally {
      setIsSaving(false);
    }
  }

  async function handlePasswordReset() {
    try {
      setIsSendingReset(true);

      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE_URL}/api/auth/password-reset/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));
      console.log("password reset response:", data);
      if (!response.ok) {
        throw new Error(
          data?.detail ||
            t(
              "messages.passwordResetError",
              "Could not send the password reset email.",
            ),
        );
      }

      setFeedbackModal({
        open: true,
        type: "success",
        title: t("messages.successTitle", "Email sent"),
        text: t(
          "messages.passwordResetSent",
          "A password reset email has been sent to your account email address.",
        ),
      });
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t(
            "messages.passwordResetError",
            "Could not send the password reset email.",
          ),
      });
    } finally {
      setIsSendingReset(false);
    }
  }

  function handleProfileImageChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_PROFILE_IMAGE_TYPES.includes(file.type)) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text: t(
          "messages.profileImageTypeError",
          "Please upload a PNG, JPG, JPEG, WEBP or SVG image.",
        ),
      });
      event.target.value = "";
      return;
    }

    const maxBytes = MAX_PROFILE_IMAGE_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text: t(
          "messages.profileImageSizeError",
          `The image must be smaller than ${MAX_PROFILE_IMAGE_SIZE_MB} MB.`,
        ),
      });
      event.target.value = "";
      return;
    }

    setFormData((prev) => ({
      ...prev,
      profile_image_file: file,
      profile_image_url: "",
      remove_profile_image: false,
    }));
  }

  function handleRemoveProfileImage() {
    setFormData((prev) => ({
      ...prev,
      profile_image_file: null,
      profile_image_url: "",
      remove_profile_image: true,
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function triggerProfileImagePicker() {
    fileInputRef.current?.click();
  }

  useEffect(() => {
    if (formData.profile_image_file instanceof File) {
      const objectUrl = URL.createObjectURL(formData.profile_image_file);
      setProfileImagePreview(objectUrl);

      return () => URL.revokeObjectURL(objectUrl);
    }

    setProfileImagePreview(formData.profile_image_url || "");
  }, [formData.profile_image_file, formData.profile_image_url]);

  if (isLoading) {
    return (
      <div className="hb-account-page">
        <div className="hb-account-page__shell">
          <div className="hb-account-hero">
            <h1 className="hb-account-hero__title">{t("title", "Account")}</h1>
            <p className="hb-account-hero__subtitle">
              {t("messages.loading", "Loading your account information...")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  function cancelLeavePage() {
    setLeaveModal(false);
    setPendingNavigationPath("");
  }

  function confirmLeavePage() {
    clearStorage(HB_ACCOUNT_FORM_STORAGE_KEY);
    setAccountDirty?.(false);
    setLeaveModal(false);

    if (pendingNavigationPath) {
      navigate(pendingNavigationPath);
    }

    setPendingNavigationPath("");
  }

  function resetFromInitialData() {
    setFormData(initialData);
    setProfileImagePreview(initialData.profile_image_url || "");
    clearStorage(HB_ACCOUNT_FORM_STORAGE_KEY);
    setAccountDirty?.(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function requestDiscardConfirmation() {
    setConfirmModal({
      open: true,
      type: "DISCARD_CHANGES",
      title: t("confirm.discardTitle", "Discard changes?"),
      text: t(
        "confirm.discardText",
        "Your unsaved account changes will be lost.",
      ),
      confirmLabel: t("confirm.discard", "Discard"),
    });
  }

  function requestPasswordResetConfirmation() {
    setConfirmModal({
      open: true,
      type: "PASSWORD_RESET",
      title: t("confirm.passwordResetTitle", "Send password reset email?"),
      text: t(
        "confirm.passwordResetText",
        "A secure password reset link will be sent to {{email}}.",
        {
          email: formData.email,
        },
      ),
      confirmLabel: t("confirm.sendEmail", "Send email"),
    });
  }

  function closeConfirmModal() {
    setConfirmModal({
      open: false,
      type: "",
      title: "",
      text: "",
      confirmLabel: "",
    });
  }

  async function handleConfirmAction() {
    if (confirmModal.type === "PASSWORD_RESET") {
      closeConfirmModal();
      await handlePasswordReset();
      return;
    }

    if (confirmModal.type === "DISCARD_CHANGES") {
      closeConfirmModal();
      resetFromInitialData();
    }
  }

  if (isAuthLoading) return;

  if (!isAuthenticated) {
    setIsLoading(false);
    return;
  }

  return (
    <>
      <div className="hb-account-page">
        <div className="hb-account-page__shell">
          <div className="hb-account-hero">
            <h1 className="hb-account-hero__title">{t("title", "Account")}</h1>
            <p className="hb-account-hero__subtitle">
              {t(
                "subtitle",
                "Manage your personal information, contact details and account settings.",
              )}
            </p>
          </div>

          <div className="hb-account-card">
            <form className="hb-account-form" onSubmit={handleSubmit}>
              <section className="hb-account-block">
                <h2 className="hb-account-block__title">
                  {t("sections.personalInformation", "Personal information")}
                </h2>

                <div className="hb-account-panel">
                  <div className="hb-account-avatar-block">
                    <div className="hb-account-avatar-block__left">
                      <FieldLabel
                        label={t("fields.profileImage", "Profile image")}
                        tooltip={t(
                          "tooltips.profileImage",
                          "Upload a personal image to make your profile easier to recognise across the platform.",
                        )}
                      />

                      <p className="hb-account-avatar-block__help">
                        {t(
                          "profileImage.help",
                          "Recommended: square image, PNG/JPG/WEBP/SVG, up to 12 MB.",
                        )}
                      </p>

                      <div className="hb-account-avatar-actions">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".png,.jpg,.jpeg,.webp"
                          onChange={handleProfileImageChange}
                          className="hb-account-avatar-input"
                        />

                        <button
                          type="button"
                          className="hb-account-avatar-btn"
                          onClick={triggerProfileImagePicker}
                        >
                          {profileImagePreview
                            ? t("buttons.replaceImage", "Replace image")
                            : t("buttons.uploadImage", "Upload image")}
                        </button>

                        {profileImagePreview ? (
                          <button
                            type="button"
                            className="hb-account-avatar-btn hb-account-avatar-btn--ghost"
                            onClick={handleRemoveProfileImage}
                          >
                            {t("buttons.removeImage", "Remove")}
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div className="hb-account-avatar-preview">
                      {profileImagePreview ? (
                        <img
                          src={profileImagePreview}
                          alt={t("fields.profileImage", "Profile image")}
                          className="hb-account-avatar-preview__image"
                        />
                      ) : (
                        <div className="hb-account-avatar-placeholder">
                          <span>
                            {formData.first_name?.[0] || ""}
                            {formData.last_name?.[0] || ""}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="hb-account-grid">
                    <div className="hb-account-field">
                      <FieldLabel
                        htmlFor="hb-first-name"
                        label={t("fields.firstName", "First name")}
                        tooltip={t(
                          "tooltips.firstName",
                          "Your first name as stored in your account.",
                        )}
                      />

                      <input
                        id="hb-first-name"
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        autoComplete="given-name"
                        required
                      />
                    </div>

                    <div className="hb-account-field">
                      <FieldLabel
                        label={t("fields.lastName", "Last name")}
                        tooltip={t(
                          "tooltips.lastName",
                          "Your last name as stored in your account.",
                        )}
                      />
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        autoComplete="family-name"
                        required
                      />
                    </div>

                    <div className="hb-account-field">
                      <FieldLabel
                        label={t("fields.dateOfBirth", "Date of birth")}
                        tooltip={t(
                          "tooltips.dateOfBirth",
                          "This can be reused from your dossier draft if available.",
                        )}
                      />
                      <input
                        type="text"
                        name="date_of_birth"
                        value={formData.date_of_birth}
                        onChange={handleDateOfBirthChange}
                        placeholder="DD/MM/YYYY"
                        inputMode="numeric"
                        maxLength={10}
                        autoComplete="bday"
                      />
                    </div>

                    <div className="hb-account-field">
                      <FieldLabel
                        label={t("fields.nationality", "Nationality")}
                        tooltip={t(
                          "tooltips.nationality",
                          "This can be reused from your dossier draft if available.",
                        )}
                      />
                      <div className="hb-account-select-wrap">
                        <select
                          name="nationality"
                          value={formData.nationality}
                          onChange={handleChange}
                          autoComplete="country"
                        >
                          <option value="">
                            {t("fields.selectCountry", "Select country")}
                          </option>
                          {EUROPEAN_COUNTRIES.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          size={18}
                          className="hb-account-select-wrap__icon"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="hb-account-block">
                <h2 className="hb-account-block__title">
                  {t("sections.contactDetails", "Contact details")}
                </h2>

                <div className="hb-account-panel">
                  <div className="hb-account-grid">
                    <div className="hb-account-field">
                      <FieldLabel
                        label={t("fields.emailAddress", "Email address")}
                        tooltip={t(
                          "tooltips.emailAddress",
                          "This email comes from your account and is not editable here.",
                        )}
                      />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        autoComplete="email"
                        required
                        disabled
                      />
                    </div>

                    <div className="hb-account-field">
                      <FieldLabel
                        label={t("fields.phoneNumber", "Phone number")}
                        tooltip={t(
                          "tooltips.phoneNumber",
                          "This can be reused from your dossier draft if available.",
                        )}
                      />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        autoComplete="tel"
                        required
                      />
                    </div>

                    <div className="hb-account-field">
                      <FieldLabel
                        label={t(
                          "fields.countryOfResidence",
                          "Country of residence",
                        )}
                        tooltip={t(
                          "tooltips.countryOfResidence",
                          "The country where you currently live.",
                        )}
                      />
                      <div className="hb-account-select-wrap">
                        <select
                          name="country_of_residence"
                          value={formData.country_of_residence}
                          onChange={handleChange}
                        >
                          <option value="">
                            {t("fields.selectCountry", "Select country")}
                          </option>
                          {COUNTRIES.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          size={18}
                          className="hb-account-select-wrap__icon"
                        />
                      </div>
                    </div>

                    <div className="hb-account-field">
                      <FieldLabel
                        label={t(
                          "fields.nationalIdNumber",
                          "National ID number",
                        )}
                        tooltip={t(
                          "tooltips.nationalIdNumber",
                          "Your identification number used in your profile.",
                        )}
                      />
                      <input
                        type="text"
                        name="national_id_number"
                        value={formData.national_id_number}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="hb-account-field hb-account-field--full">
                      <FieldLabel
                        label={t("fields.address", "Address")}
                        tooltip={t(
                          "tooltips.address",
                          "This can be reused from your dossier draft if available.",
                        )}
                      />
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        autoComplete="street-address"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section className="hb-account-block">
                <h2 className="hb-account-block__title">
                  {t("sections.security", "Security")}
                </h2>

                <div className="hb-account-panel hb-account-panel--security">
                  <div className="hb-account-security">
                    <div className="hb-account-security__info">
                      <div className="hb-account-security__icon">
                        <KeyRound size={18} />
                      </div>
                      <div>
                        <h3 className="hb-account-security__title">
                          {t("security.passwordTitle", "Password")}
                        </h3>
                        <p className="hb-account-security__text">
                          {t(
                            "security.passwordText",
                            "Password changes are handled securely through Auth0. We will send a reset link to your account email.",
                          )}
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="hb-account-reset-btn"
                      onClick={requestPasswordResetConfirmation}
                      disabled={isSendingReset}
                    >
                      {isSendingReset
                        ? t("buttons.sending", "Sending...")
                        : t(
                            "buttons.sendPasswordReset",
                            "Send password reset email",
                          )}
                    </button>
                  </div>
                </div>
              </section>

              <div className="hb-account-actions">
                <button
                  type="button"
                  className="hb-account-discard-btn"
                  onClick={requestDiscardConfirmation}
                  disabled={!hasChanges || isSaving}
                >
                  <RotateCcw size={16} />
                  {t("buttons.discard", "Discard")}
                </button>

                <button
                  type="submit"
                  className="hb-account-save-btn"
                  disabled={!hasChanges || isSaving}
                >
                  <Save size={16} />
                  {isSaving
                    ? t("buttons.saving", "Saving...")
                    : t("buttons.saveChanges", "Save changes")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {feedbackModal.open ? (
        <div className="hb-account-modal-overlay" onClick={closeFeedbackModal}>
          <div
            className={`hb-account-modal ${
              feedbackModal.type === "error"
                ? "hb-account-modal--error"
                : "hb-account-modal--success"
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hb-account-modal__icon">
              {feedbackModal.type === "error" ? (
                <AlertCircle size={22} />
              ) : (
                <CheckCircle2 size={22} />
              )}
            </div>

            <div className="hb-account-modal__content">
              <h3>{feedbackModal.title}</h3>
              <p>{feedbackModal.text}</p>
            </div>

            <button
              type="button"
              className="hb-account-modal__close"
              onClick={closeFeedbackModal}
              aria-label={t("common.close", "Close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}
      {confirmModal.open ? (
        <div className="hb-account-confirm-overlay" onClick={closeConfirmModal}>
          <div
            className={`hb-account-confirm ${
              confirmModal.type === "PASSWORD_RESET"
                ? "hb-account-confirm--security"
                : "hb-account-confirm--warning"
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="hb-account-confirm-title"
            aria-describedby="hb-account-confirm-text"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hb-account-confirm__icon">
              <AlertCircle size={22} />
            </div>

            <div className="hb-account-confirm__content">
              <h3>{confirmModal.title}</h3>
              <p>{confirmModal.text}</p>

              <div className="hb-account-confirm__actions">
                <button
                  type="button"
                  className="hb-account-confirm__cancel"
                  onClick={closeConfirmModal}
                >
                  {t("confirm.cancel", "Cancel")}
                </button>

                <button
                  type="button"
                  className="hb-account-confirm__confirm"
                  onClick={handleConfirmAction}
                  disabled={
                    confirmModal.type === "PASSWORD_RESET" && isSendingReset
                  }
                >
                  {confirmModal.type === "PASSWORD_RESET" && isSendingReset
                    ? t("buttons.sending", "Sending...")
                    : confirmModal.confirmLabel}
                </button>
              </div>
            </div>

            <button
              type="button"
              className="hb-account-confirm__close"
              onClick={closeConfirmModal}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}

      {leaveModal ? (
        <div className="hb-account-confirm-overlay" onClick={cancelLeavePage}>
          <div
            className="hb-account-confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hb-account-confirm__icon">
              <AlertCircle size={22} />
            </div>

            <div className="hb-account-confirm__content">
              <h3>{t("confirm.leaveTitle", "Leave without saving?")}</h3>
              <p>
                {t(
                  "confirm.leaveText",
                  "You have unsaved account changes. If you leave now, they will be lost.",
                )}
              </p>

              <div className="hb-account-confirm__actions">
                <button
                  type="button"
                  className="hb-account-confirm__cancel"
                  onClick={cancelLeavePage}
                >
                  {t("confirm.stay", "Stay")}
                </button>

                <button
                  type="button"
                  className="hb-account-confirm__confirm"
                  onClick={confirmLeavePage}
                >
                  {t("confirm.leave", "Leave")}
                </button>
              </div>
            </div>

            <button
              type="button"
              className="hb-account-confirm__close"
              onClick={cancelLeavePage}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
